import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../components/Logo";
import client from "../lib/api";
import { toast } from "sonner";
import { trackConversion, getAttribution } from "../lib/analytics";
import {
  Calendar, Clock, CheckCircle, ArrowRight, Sparkle, VideoCamera,
  Truck, ArrowLeft, Star
} from "@phosphor-icons/react";

/*
  Book-a-Demo page — simple slot picker for prospects.
  Slots submitted to /api/contact with source="demo_booking".
  Mike gets an email notification with the requested time.
*/

const NEXT_DAYS = 7;

function buildSlots() {
  const days = [];
  const now = new Date();
  let d = new Date(now);
  d.setHours(0, 0, 0, 0);
  while (days.length < NEXT_DAYS) {
    d = new Date(d.getTime() + 24 * 60 * 60 * 1000);
    const day = d.getDay(); // 0 Sun ... 6 Sat
    if (day >= 1 && day <= 5) {
      // weekdays only
      days.push(new Date(d));
    }
  }
  const times = ["9:00 AM", "10:30 AM", "1:00 PM", "2:30 PM", "4:00 PM"];
  return { days, times };
}

const { days: DAYS, times: TIMES } = buildSlots();

export default function BookDemoPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({
    contact_name: "",
    company_name: "",
    email: "",
    phone: "",
    fleet_size: "",
    current_challenges: "",
  });

  // Pre-fill from URL params — happens when a user is redirected here from
  // /signup after picking a 26+ truck fleet size.
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      contact_name: prev.contact_name || params.get("name") || "",
      company_name: prev.company_name || params.get("company") || "",
      email: prev.email || params.get("email") || "",
      phone: prev.phone || params.get("phone") || "",
      fleet_size: prev.fleet_size || params.get("fleet_size") || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const canSubmit =
    selectedDate && selectedTime && form.contact_name && form.company_name && form.email && form.phone && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const preferred_time = `${selectedDate.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })} at ${selectedTime} Central`;
      await client.post("/contact", {
        ...form,
        source: "demo_booking",
        services_of_interest: "Live demo request",
        preferred_time,
        ...getAttribution(),
      });
      setDone(true);
      toast.success("Demo request sent!");
      trackConversion("demo_book", { preferred_time, fleet_size: form.fleet_size });
    } catch {
      toast.error("Something went wrong. Try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen relative noise-bg flex flex-col" data-testid="demo-booked">
        <header className="border-b border-white/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
            <Link to="/"><Logo /></Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="card-surface p-10 sm:p-14 max-w-lg w-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} weight="fill" color="#34D399" />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">You're booked.</h1>
            <p className="text-neutral-400 mt-4 leading-relaxed">
              I'll email you a calendar invite for <span className="text-white font-medium">{selectedDate.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })} at {selectedTime} Central</span> within a few hours. Look for a message from <span className="text-[#FF5A1F]">contact@freightpilotai.ai</span>.
            </p>
            <div className="mt-8 pt-6 border-t border-white/10 text-left">
              <div className="overline mb-3">WHILE YOU WAIT</div>
              <ul className="space-y-2 text-sm text-neutral-300">
                <li className="flex items-start gap-2">
                  <Sparkle size={14} weight="fill" className="text-[#FF5A1F] mt-0.5 shrink-0" />
                  Try the AI Playground on the homepage
                </li>
                <li className="flex items-start gap-2">
                  <Truck size={14} className="text-[#FF5A1F] mt-0.5 shrink-0" />
                  Explore the interactive demo dashboard
                </li>
                <li className="flex items-start gap-2">
                  <VideoCamera size={14} className="text-[#FF5A1F] mt-0.5 shrink-0" />
                  I'll bring a workflow review tailored to your fleet size
                </li>
              </ul>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate("/")}
                className="flex-1 rounded-full px-5 py-3 text-sm border border-white/15 hover:border-white/30 text-white transition-colors"
                data-testid="demo-booked-home"
              >
                Back to home
              </button>
              <a
                href="https://freightpilotai.ai"
                className="flex-1 btn-primary rounded-full px-5 py-3 text-sm inline-flex items-center justify-center gap-2"
                data-testid="demo-booked-trydemo"
              >
                Try the demo <ArrowRight size={14} weight="bold" />
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative noise-bg" data-testid="demo-page">
      <header className="border-b border-white/10 sticky top-0 backdrop-blur-md bg-[#0A0A0A]/85 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link
            to="/"
            className="text-xs text-neutral-500 hover:text-white transition-colors inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          {/* Left column — pitch */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 pill pill-primary mb-4">
              <VideoCamera size={12} weight="fill" /> 30-MIN LIVE DEMO
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
              See FreightPilot AI™ on <span className="text-[#FF5A1F]">your loads</span>.
            </h1>
            <p className="text-neutral-400 mt-5 text-sm sm:text-base leading-relaxed">
              A 30-minute screen-share where I'll show you the dashboard with your actual routes,
              draft dispatch messages for your drivers, and calculate profit on a real load offer.
              No slides, no pitch — just proof it works for your fleet.
            </p>

            <div className="mt-8 space-y-3 text-sm">
              {[
                "AI drafts a dispatch message for one of your drivers, live",
                "Profit calculator run on a real load offer you bring",
                "Q&A + custom pricing for your fleet size",
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-2 text-neutral-300">
                  <CheckCircle size={16} weight="fill" className="text-[#FF5A1F] mt-0.5 shrink-0" />
                  <span>{line}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#141414] flex items-center justify-center shrink-0">
                  <span className="font-display text-sm font-semibold text-white">MG</span>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">Mike Gee</div>
                  <div className="text-xs text-neutral-500">Founder · FreightPilot AI</div>
                </div>
              </div>
              <div className="flex items-center gap-0.5 mt-3 text-[#FF5A1F]">
                {[0, 1, 2, 3, 4].map(i => <Star key={i} size={12} weight="fill" />)}
                <span className="text-xs text-neutral-500 ml-2">Rated 5/5 by pilot fleets</span>
              </div>
            </div>
          </div>

          {/* Right column — form */}
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="card-surface p-6 sm:p-8" data-testid="demo-form">
              {/* Step 1: Pick a day */}
              <div className="mb-8">
                <div className="overline mb-3 flex items-center gap-2">
                  <Calendar size={12} className="text-[#FF5A1F]" /> 1. PICK A DAY
                </div>
                <div className="grid grid-cols-5 gap-2" data-testid="demo-days">
                  {DAYS.map((d) => {
                    const active = selectedDate?.toDateString() === d.toDateString();
                    return (
                      <button
                        key={d.toISOString()}
                        type="button"
                        onClick={() => setSelectedDate(d)}
                        data-testid={`demo-day-${d.toISOString().slice(0, 10)}`}
                        className={`p-3 rounded-lg border text-center transition-colors ${
                          active
                            ? "bg-[#FF5A1F] border-[#FF5A1F] text-black"
                            : "bg-transparent border-white/15 text-neutral-300 hover:border-white/30 hover:text-white"
                        }`}
                      >
                        <div className={`text-[10px] uppercase tracking-widest ${active ? "text-black/60" : "text-neutral-500"}`}>
                          {d.toLocaleDateString([], { weekday: "short" })}
                        </div>
                        <div className="text-lg font-semibold mt-0.5">{d.getDate()}</div>
                        <div className={`text-[10px] ${active ? "text-black/60" : "text-neutral-600"}`}>
                          {d.toLocaleDateString([], { month: "short" })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2: Pick a time */}
              <div className="mb-8">
                <div className="overline mb-3 flex items-center gap-2">
                  <Clock size={12} className="text-[#FF5A1F]" /> 2. PICK A TIME (CENTRAL)
                </div>
                <div className="flex flex-wrap gap-2" data-testid="demo-times">
                  {TIMES.map((t) => {
                    const active = selectedTime === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setSelectedTime(t)}
                        disabled={!selectedDate}
                        data-testid={`demo-time-${t.replace(/[^a-z0-9]/gi, "")}`}
                        className={`px-4 py-2 rounded-full text-sm border transition-colors disabled:opacity-40 ${
                          active
                            ? "bg-[#FF5A1F] border-[#FF5A1F] text-black"
                            : "bg-transparent border-white/15 text-neutral-300 hover:border-white/30 hover:text-white"
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 3: Your info */}
              <div className="mb-6">
                <div className="overline mb-3 flex items-center gap-2">
                  <Truck size={12} className="text-[#FF5A1F]" /> 3. YOUR INFO
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input placeholder="Your name" testid="demo-name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
                  <Input placeholder="Company name" testid="demo-company" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
                  <Input placeholder="Email" type="email" testid="demo-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                  <Input placeholder="Phone" testid="demo-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                </div>
                <select
                  value={form.fleet_size}
                  onChange={(e) => setForm({ ...form, fleet_size: e.target.value })}
                  data-testid="demo-fleet"
                  className="mt-3 w-full bg-[#141414] border border-white/15 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5A1F]"
                >
                  <option value="">Fleet size (optional)</option>
                  <option>1 truck (owner-op)</option>
                  <option>2-3 trucks</option>
                  <option>4-10 trucks</option>
                  <option>11-25 trucks</option>
                  <option>26+ trucks</option>
                </select>
                <textarea
                  value={form.current_challenges}
                  onChange={(e) => setForm({ ...form, current_challenges: e.target.value })}
                  placeholder="What's the biggest bottleneck in your ops right now? (optional)"
                  rows={2}
                  data-testid="demo-challenges"
                  className="mt-3 w-full bg-[#141414] border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F] resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="demo-submit"
                className="w-full btn-primary rounded-full px-6 py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Book my demo"}
                <ArrowRight size={13} weight="bold" />
              </button>
              <p className="text-[10px] text-neutral-600 text-center mt-3">
                By booking, you agree to receive a calendar invite from contact@freightpilotai.ai. No spam, ever.
              </p>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

function Input({ placeholder, type = "text", value, onChange, testid }) {
  return (
    <input
      type={type}
      value={value}
      required
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-[#141414] border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]"
    />
  );
}
